import React, { useMemo, useCallback } from 'react';
import { shortPropertyName } from '../lib/utils';
import { MonthlyHeatmap } from './MonthlyHeatmap';

/**
 * Property-by-month heatmap. Thin wrapper around MonthlyHeatmap
 * that handles property-specific concerns: shortPropertyName labels,
 * property_id keys, and pre-listing gray-out logic.
 */
// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function PropertyMonthHeatmap(props) {
  const {
    properties,
    valueAccessor,
    colorScale = 'blue',
    formatter,
    emptyLabel = '\u2013',
    title,
    subtitle,
    monthTotalAccessor,
    monthTotalFormatter,
    ytdAccessor,
    ytdLabel = 'YTD',
    ytdFormatter,
    ytdTotal,
    forecastMonthStart,
    forecastMonthEnd,
  } = props;
  // Map properties to generic rows with id/name
  const rows = useMemo(() =>
    properties.map(p => ({ ...p, id: p.property_id })),
    [properties]
  );

  const getRowLabel = useCallback((row) => shortPropertyName(row.name), []);

  // Pre-listing empty cell detection
  const isEmptyCell = useCallback((row, mi, value) => {
    const monthKey = row.monthly[mi]?.month;
    const beforeListed = row.first_listed_date && monthKey && monthKey < row.first_listed_date.substring(0, 7);
    return beforeListed || value == null;
  }, []);

  // Adapt ytdAccessor: PropertyMonthHeatmap passes (property, monthValues[]),
  // MonthlyHeatmap passes (row). Compute month values inline.
  const wrappedYtdAccessor = useMemo(() => {
    if (!ytdAccessor) return undefined;
    return (row) => {
      const monthValues = [];
      for (let mi = 0; mi < 12; mi++) {
        monthValues.push(valueAccessor(row, mi));
      }
      return ytdAccessor(row, monthValues);
    };
  }, [ytdAccessor, valueAccessor]);

  return (
    <MonthlyHeatmap
      rows={rows}
      rowHeader="Property"
      rowLabel={getRowLabel}
      valueAccessor={valueAccessor}
      colorScale={colorScale}
      formatter={formatter}
      emptyLabel={emptyLabel}
      title={title}
      subtitle={subtitle}
      isEmptyCell={isEmptyCell}
      monthTotalAccessor={monthTotalAccessor}
      monthTotalFormatter={monthTotalFormatter}
      ytdAccessor={wrappedYtdAccessor}
      ytdLabel={ytdLabel}
      ytdFormatter={ytdFormatter}
      ytdTotal={ytdTotal}
      forecastMonthStart={forecastMonthStart}
      forecastMonthEnd={forecastMonthEnd}
    />
  );
}
